import { Layout, Menu } from 'antd'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useRouteMatch } from 'react-router-dom'
import MenuBuilderNavLink from 'src/screens/app/MenuBuilderNavLink'
import VenueNavTooltipLink from 'src/screens/app/VenueNavTooltipLink'
import {
  VENUE_NAV_TOOLTIPS,
  getVenueNavProgressTooltip,
} from 'src/constants/venueNavCopy'
import GrowthEnginePrimaryNudge from 'src/components/growthEngine/GrowthEnginePrimaryNudge'
import OMSErrorBoundary from 'src/components/shared/ErrorBoundary'
import OllieVenueAssistant from 'src/components/ollie/OllieVenueAssistant'
import { OllieAssistantProvider } from 'src/components/ollie/OllieAssistantContext'
import { isVenueDashboardRoute } from 'src/components/ollie/ollieVenueRoutes'
import Loading from 'src/components/styled/Loading'
import { getVenue } from 'src/features/VenueSlice'
import { getVenueSuppliers } from 'src/features/VenueSuppliersSlice'
import { useVenueCustomRoles } from 'src/hooks/useIsBlocked'
import useGrowthEngineFeatureOpened from 'src/hooks/useGrowthEngineFeatureOpened'
import useUserRole from 'src/hooks/useUserRole'
import { shouldHidePrimaryNudgeOnVenueChrome } from 'src/utils/growthEngineOneJobPage'
import { clearGrowthEngineHomeTipBadge } from 'src/utils/growthEngineHomeTipBadge'
import {
  closeVenueNavTooltips,
  venueNavTooltipIfNotCurrent,
} from 'src/utils/venueNavTooltip'
import { venueTopNavSelectedKey } from 'src/utils/venueTopNav'
import { resetStockTakeHistoryCountTypeFilterOnTakeError } from 'src/utils/stockTakeHistoryFilters'
import useGrowthEngineHomeTipBadge from 'src/hooks/useGrowthEngineHomeTipBadge'
import usePendingApprovalsCount from 'src/hooks/usePendingApprovalsCount'
import useFoodSafetyTodayBadgeCount from 'src/hooks/useFoodSafetyTodayBadgeCount'
import useVenueHomeOutstandingCount from 'src/hooks/useVenueHomeOutstandingCount'
import useRecipeGpDashboardAlert from 'src/hooks/useRecipeGpDashboardAlert'
import {
  formatFoodSafetyTodayBadgeA11yLabel,
  formatFoodSafetyTodayBadgeLabel,
} from 'src/utils/foodSafetyHome'
import {
  formatVenueHomeBadgeA11yLabel,
  formatVenueHomeBadgeLabel,
} from 'src/utils/venueHomeApi'
import {
  canSeeDashboardTopNav,
  canSeeSpecialPurchasesTab,
  getDashboardPurchasingHomePath,
} from 'src/utils/permissions'
import styled from 'styled-components/macro'

const { Item } = Menu

const VenueInternalWrapper = ({ children, venue, loading }) => {
  const location = useLocation()
  const dispatch = useDispatch()
  const venueRouteMatch = useRouteMatch('/v/:venueId')
  const routeVenueId = venueRouteMatch?.params?.venueId
  const [failedVenueId, setFailedVenueId] = useState(null)
  const onVenueDashboard = isVenueDashboardRoute(location.pathname)
  const pathFragments = location.pathname.split('/')
  const hasSubMenu = pathFragments.includes('purchasing')
  const { _id: venueId } = venue || {}
  const currentPlan = venue?.usageLimits?.currentPlan
  const selectedNavKey = venueTopNavSelectedKey(location.pathname)

  const userRole = useUserRole()
  const customRoles = useVenueCustomRoles()
  const purchasingHomePath = getDashboardPurchasingHomePath(
    venueId,
    userRole,
    customRoles,
  )
  const canSeeNav = key => canSeeDashboardTopNav(key, userRole, customRoles)
  const homeTipReady = useGrowthEngineHomeTipBadge()
  const pendingApprovalsCount = usePendingApprovalsCount(
    venueId || routeVenueId,
  )
  const foodSafetyOpenCount = useFoodSafetyTodayBadgeCount(
    venueId || routeVenueId,
  )
  const homeOutstandingCount = useVenueHomeOutstandingCount(
    venueId || routeVenueId,
  )
  useRecipeGpDashboardAlert(
    venueId || routeVenueId,
    canSeeNav('menu-builder'),
    canSeeNav('subscriptions'),
  )

  useEffect(() => {
    if (purchasingHomePath && location.pathname === purchasingHomePath) {
      clearGrowthEngineHomeTipBadge()
    }
  }, [location.pathname, purchasingHomePath])

  useEffect(() => {
    closeVenueNavTooltips()
  }, [location.pathname])

  useEffect(() => {
    const dismiss = () => closeVenueNavTooltips()
    document.addEventListener('mousedown', dismiss, true)
    return () => document.removeEventListener('mousedown', dismiss, true)
  }, [])

  useGrowthEngineFeatureOpened({
    venueId: venueId || routeVenueId,
    pathname: location.pathname,
  })

  useEffect(() => {
    let cancelled = false
    const id = routeVenueId || venueId
    if (!id) return undefined

    setFailedVenueId(null)
    ;(async () => {
      const loaded = await dispatch(getVenue(id))
      dispatch(getVenueSuppliers(id))
      if (!cancelled && loaded === false) setFailedVenueId(String(id))
    })()

    return () => {
      cancelled = true
    }
  }, [dispatch, routeVenueId, venueId])

  if (!onVenueDashboard) {
    return <>{children}</>
  }

  const waitingForRouteVenue =
    Boolean(routeVenueId) && String(venue?._id || '') !== String(routeVenueId)

  if (waitingForRouteVenue) {
    if (failedVenueId === String(routeVenueId)) {
      return <p>Couldn&apos;t open this venue.</p>
    }
    return <Loading />
  }

  if (!venue || pathFragments[pathFragments.length - 1] === 'venues') {
    return null
  }

  return loading ? (
    <Loading />
  ) : (
    <OllieAssistantProvider>
      <StyledMenu
        mode='horizontal'
        selectedKeys={[selectedNavKey]}
        triggerSubMenuAction='click'
      >
        {canSeeNav('home') && (
          <Item key='home'>
            <VenueNavTooltipLink
              to={`/v/${venueId}/home`}
              tooltip={
                homeOutstandingCount > 0
                  ? formatVenueHomeBadgeA11yLabel(homeOutstandingCount)
                  : VENUE_NAV_TOOLTIPS.home
              }
              data-testid='venue-nav-home'
            >
              Home
              {homeOutstandingCount > 0 ? (
                <FoodSafetyOpenBadge
                  data-testid='venue-home-outstanding-badge'
                  aria-label={formatVenueHomeBadgeA11yLabel(
                    homeOutstandingCount,
                  )}
                  title={formatVenueHomeBadgeA11yLabel(homeOutstandingCount)}
                >
                  {formatVenueHomeBadgeLabel(homeOutstandingCount)}
                </FoodSafetyOpenBadge>
              ) : null}
            </VenueNavTooltipLink>
          </Item>
        )}
        {canSeeNav('purchasing') && purchasingHomePath && (
          <Item key='purchasing'>
            <VenueNavTooltipLink
              to={purchasingHomePath}
              tooltip={hasSubMenu ? null : VENUE_NAV_TOOLTIPS.purchasing}
              data-testid='venue-nav-purchasing'
            >
              Purchasing
              {homeTipReady ? (
                <HomeTipBadge data-testid='growth-engine-home-tip'>
                  Tip
                </HomeTipBadge>
              ) : null}
            </VenueNavTooltipLink>
          </Item>
        )}
        {canSeeNav('progress') && (
          <Item key='progress'>
            <VenueNavTooltipLink
              to={`/v/${venueId}/progress`}
              tooltip={venueNavTooltipIfNotCurrent(
                location.pathname,
                `/v/${venueId}/progress`,
                getVenueNavProgressTooltip(currentPlan),
              )}
              data-testid='venue-nav-progress'
            >
              Success Hub
            </VenueNavTooltipLink>
          </Item>
        )}
        {canSeeNav('suppliers') && (
          <Item key='suppliers'>
            <VenueNavTooltipLink
              to={`/v/${venueId}/suppliers`}
              tooltip={VENUE_NAV_TOOLTIPS.suppliers}
              data-testid='venue-nav-suppliers'
            >
              Your suppliers
            </VenueNavTooltipLink>
          </Item>
        )}
        {canSeeNav('catalogue') && (
          <Item key='catalogue'>
            <VenueNavTooltipLink
              to={`/v/${venueId}/catalogue`}
              tooltip={VENUE_NAV_TOOLTIPS.catalogue}
              data-testid='venue-nav-catalogue'
            >
              Catalogue
            </VenueNavTooltipLink>
          </Item>
        )}
        {canSeeNav('products') && (
          <Item key='products'>
            <VenueNavTooltipLink
              to={`/v/${venueId}/products`}
              tooltip={VENUE_NAV_TOOLTIPS.products}
              data-testid='venue-nav-products'
            >
              Shopping lists
            </VenueNavTooltipLink>
          </Item>
        )}
        {canSeeNav('stockTakes') && (
          <Item key='stockTakes'>
            <VenueNavTooltipLink
              to={`/v/${venueId}/stockTakes`}
              tooltip={VENUE_NAV_TOOLTIPS.stockTakes}
              data-testid='venue-nav-stock'
            >
              Stock
            </VenueNavTooltipLink>
          </Item>
        )}
        {canSeeNav('food-safety') && (
          <Item key='food-safety'>
            <VenueNavTooltipLink
              to={`/v/${venueId}/food-safety`}
              tooltip={
                foodSafetyOpenCount > 0
                  ? formatFoodSafetyTodayBadgeA11yLabel(foodSafetyOpenCount)
                  : VENUE_NAV_TOOLTIPS['food-safety']
              }
              data-testid='venue-nav-food-safety'
            >
              Food Safety
              {foodSafetyOpenCount > 0 ? (
                <FoodSafetyOpenBadge
                  data-testid='food-safety-open-badge'
                  aria-label={formatFoodSafetyTodayBadgeA11yLabel(
                    foodSafetyOpenCount,
                  )}
                  title={formatFoodSafetyTodayBadgeA11yLabel(
                    foodSafetyOpenCount,
                  )}
                >
                  {formatFoodSafetyTodayBadgeLabel(foodSafetyOpenCount)}
                </FoodSafetyOpenBadge>
              ) : null}
            </VenueNavTooltipLink>
          </Item>
        )}
        {canSeeNav('approvals') && (
          <Item key='approvals'>
            <VenueNavTooltipLink
              to={`/v/${venueId}/approvals`}
              tooltip={VENUE_NAV_TOOLTIPS.approvals}
              data-testid='venue-nav-approvals'
            >
              Approvals
              {pendingApprovalsCount > 0 ? (
                <PendingApprovalsBadge data-testid='pending-approvals-badge'>
                  {pendingApprovalsCount > 99 ? '99+' : pendingApprovalsCount}
                </PendingApprovalsBadge>
              ) : null}
            </VenueNavTooltipLink>
          </Item>
        )}
        {canSeeNav('menu-builder') && (
          <Item key='menu-builder'>
            <MenuBuilderNavLink venueId={venueId} />
          </Item>
        )}
        {canSeeNav('settings') && (
          <Item key='settings'>
            <VenueNavTooltipLink
              to={`/v/${venueId}/settings`}
              tooltip={VENUE_NAV_TOOLTIPS.settings}
              data-testid='venue-nav-settings'
            >
              Settings
            </VenueNavTooltipLink>
          </Item>
        )}
        {canSeeSpecialPurchasesTab(venue) && (
          <Item key='special-purchases'>
            <VenueNavTooltipLink
              to={`/v/${venueId}/special-purchases`}
              tooltip={VENUE_NAV_TOOLTIPS['special-purchases']}
              data-testid='venue-nav-special-purchases'
            >
              Special Purchases
            </VenueNavTooltipLink>
          </Item>
        )}
      </StyledMenu>
      <StyledLayout hassubmenu={hasSubMenu ? 1 : 0}>
        {!shouldHidePrimaryNudgeOnVenueChrome(
          location.pathname,
          location.search,
        ) && !/\/home\/?$/.test(location.pathname || '') ? (
          <GrowthEnginePrimaryNudge
            venueId={venueId}
            currentPlan={currentPlan}
            insetForSubmenu={hasSubMenu}
          />
        ) : null}
        <OMSErrorBoundary
          onError={() =>
            resetStockTakeHistoryCountTypeFilterOnTakeError({
              venueId: venueId || routeVenueId,
              pathname: location.pathname,
            })
          }
        >
          {children}
        </OMSErrorBoundary>
      </StyledLayout>
      <OllieVenueAssistant />
    </OllieAssistantProvider>
  )
}

const HomeTipBadge = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 0 6px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
  color: ${({ theme }) => theme.colours.bbqBlack || '#262626'};
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  vertical-align: middle;
`

/** Outstanding count on the Approvals tab — antd orange, same as the other work badges. */
const PendingApprovalsBadge = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 0 6px;
  border-radius: 999px;
  background: #fa8c16;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  vertical-align: middle;
`

/** Today’s still-to-do sections on Food Safety — same orange work pill. */
const FoodSafetyOpenBadge = styled(PendingApprovalsBadge)``

const StyledMenu = styled(Menu)`
  height: 52px;
  background: ${({ theme }) => theme.colours.mayoWhite};
  padding: 0;
  min-width: 0;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  /* rc-menu measures item widths to move overflow into its More submenu. */
  > .ant-menu-item,
  > .ant-menu-submenu {
    flex: 0 0 auto;
  }

  /* rc-menu inserts its hidden overflow trigger before the first tab. */
  > .ant-menu-item:nth-child(2) {
    padding-left: 12px;
  }

  /* Clearfix pseudo-elements otherwise take a share of the flex spacing. */
  &::before,
  &::after {
    display: none;
  }
`

const StyledLayout = styled(Layout)`
  background: ${({ theme }) => theme.colours.mayoWhite};
  min-height: calc(100vh - 167px) !important;
  overflow: visible;
  padding: ${({ theme, hassubmenu }) =>
    hassubmenu ? 0 : `${theme.spacing.lg} ${theme.spacing.xxl}`};
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: ${({ theme, hassubmenu }) =>
      hassubmenu ? 0 : `${theme.spacing.lg} ${theme.spacing.md}`};
  }
`

export default VenueInternalWrapper
